// =============================================================================
// hooks/useExternalCallLog.ts — TanStack Query hooks for external_call_log
// =============================================================================
// SHARED-3 / CLINIO-193
//
// Provides:
//   useExternalCallLogList({ targetSystem, direction, limit })
//     — paginated list of log rows, newest first.
//   useExternalCallLogStats({ targetSystem, since })
//     — aggregated stats: count by HTTP status, p95 duration_ms, error rate.
//
// Access is controlled by RLS on the server:
//   - Platform admins see all rows.
//   - Clinic admins see only their clinic's rows.
// =============================================================================

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

// ---------------------------------------------------------------------------
// Shared types
// ---------------------------------------------------------------------------

export type TargetSystem = "clinio_pay" | "clinio_forms";
export type Direction    = "outbound" | "inbound";

export interface ExternalCallLogRow {
  id:                    string;
  request_id:            string;
  clinic_id:             string | null;
  user_id:               string | null;
  direction:             Direction;
  target_system:         TargetSystem;
  endpoint:              string;
  http_method:           string;
  http_status:           number | null;
  duration_ms:           number | null;
  request_body_preview:  Record<string, unknown> | null;
  response_body_preview: Record<string, unknown> | null;
  error_code:            string | null;
  error_message:         string | null;
  created_at:            string;
}

// ---------------------------------------------------------------------------
// useExternalCallLogList
// ---------------------------------------------------------------------------

export interface UseExternalCallLogListArgs {
  targetSystem: TargetSystem;
  direction:    Direction;
  /** Number of rows to return. Defaults to 50. */
  limit?:       number;
}

export function useExternalCallLogList({
  targetSystem,
  direction,
  limit = 50,
}: UseExternalCallLogListArgs) {
  return useQuery({
    queryKey: ["external_call_log", "list", targetSystem, direction, limit],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("external_call_log")
        .select(
          "id, request_id, clinic_id, user_id, direction, target_system, " +
          "endpoint, http_method, http_status, duration_ms, " +
          "request_body_preview, response_body_preview, " +
          "error_code, error_message, created_at",
        )
        .eq("target_system", targetSystem)
        .eq("direction", direction)
        .order("created_at", { ascending: false })
        .limit(limit);

      if (error) throw error;
      return (data ?? []) as unknown as ExternalCallLogRow[];
    },
    refetchInterval: 10_000,
    staleTime:       5_000,
  });
}

// ---------------------------------------------------------------------------
// useExternalCallLogStats
// ---------------------------------------------------------------------------

export interface UseExternalCallLogStatsArgs {
  targetSystem: TargetSystem;
  /** ISO-8601 timestamp — look at rows created after this point. */
  since:        string;
}

export interface StatusBucket {
  status:  number | null;
  count:   number;
}

export interface ExternalCallLogStats {
  totalCount:       number;
  errorCount:       number;
  errorRate:        number;        // 0–1
  p95DurationMs:    number | null;
  statusBuckets:    StatusBucket[];
}

export function useExternalCallLogStats({
  targetSystem,
  since,
}: UseExternalCallLogStatsArgs) {
  return useQuery<ExternalCallLogStats>({
    queryKey: ["external_call_log", "stats", targetSystem, since],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("external_call_log")
        .select("http_status, duration_ms, error_code")
        .eq("target_system", targetSystem)
        .gte("created_at", since)
        .order("created_at", { ascending: false })
        .limit(1000);  // cap to avoid client-side OOM

      if (error) throw error;
      const rows = (data ?? []) as Array<{
        http_status: number | null;
        duration_ms: number | null;
        error_code:  string | null;
      }>;

      const totalCount = rows.length;
      const errorCount = rows.filter(
        (r) =>
          r.error_code !== null ||
          (r.http_status !== null && r.http_status >= 500),
      ).length;
      const errorRate = totalCount > 0 ? errorCount / totalCount : 0;

      // p95 duration
      const durations = rows
        .map((r) => r.duration_ms)
        .filter((d): d is number => d !== null)
        .sort((a, b) => a - b);
      const p95Index   = Math.floor(durations.length * 0.95);
      const p95DurationMs = durations.length > 0 ? (durations[p95Index] ?? null) : null;

      // Status code buckets
      const bucketMap = new Map<number | null, number>();
      for (const r of rows) {
        const bucket = r.http_status !== null ? Math.floor(r.http_status / 100) * 100 : null;
        bucketMap.set(bucket, (bucketMap.get(bucket) ?? 0) + 1);
      }
      const statusBuckets: StatusBucket[] = Array.from(bucketMap.entries())
        .map(([status, count]) => ({ status, count }))
        .sort((a, b) => (a.status ?? 0) - (b.status ?? 0));

      return { totalCount, errorCount, errorRate, p95DurationMs, statusBuckets };
    },
    refetchInterval: 10_000,
    staleTime:       5_000,
  });
}
